class CartProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.btnClose = this.querySelector('.cart_product_recommendations-close');
    this.btnClose.addEventListener('click', this.onClose);
    this.slider = this.querySelector('custom-slider');
    this.classes = {
      hide: 'hide',
    };
    this.lastItems = [];
    this.checkCart();
    this.initCartObserver();
  }

  handleAddToCart = async (e) => {
    const el = e.currentTarget;
    const handle = el.getAttribute('data-product-handle');

    if (!handle) {
      return;
    }

    el.classList.add('btn--loading');

    const productData = await this.getProduct({ handle });

    if (!productData) {
      return;
    }

    const targetVariant =
      productData.variants.find(
        (variant) =>
          this.lastItems.some((item) =>
            variant.options.includes(item.option_value.value),
          ) && variant.available,
      ) || productData.variants.find((variant) => variant.available);

    if (!targetVariant) {
      return;
    }

    const addResponse = await this.onAddToCart({
      variantId: targetVariant.id,
      quantity: 1,
    });

    if (addResponse) {
      document.dispatchEvent(new Event('cart:build'));
    }

    el.classList.remove('btn--loading');
  };

  checkCart = async () => {
    const cart = await this.getCart();

    if (!cart.items.length) {
      return;
    }

    const itemsMap = cart.items.map((item) => {
      return {
        product_id: item.product_id,
        option_value: item.options_with_values.at(0),
      };
    });

    if (!itemsMap.length) {
      return;
    }

    if (this.arrayChecker({ arr1: itemsMap, arr2: this.lastItems })) {
      return;
    }

    this.lastItems = itemsMap;

    const newItems = await this.getProductRecommendations({
      targetItems: itemsMap,
    });

    if (!newItems?.length) {
      this.classList.add(this.classes.hide);
    } else {
      this.classList.remove(this.classes.hide);
    }

    this.setNewItems({ items: newItems });

    if (newItems.length > 0) {
      this.slider.setAttribute('data-min-items-amount', newItems.length);
    }

    this.slider?.dispatchEvent(new CustomEvent('build:slider'));
  };

  setNewItems = ({ items }) => {
    if (!items?.length) {
      return;
    }

    const container = this.slider.querySelector(
      '.custom-slider-container-list',
    );

    if (!container) {
      return;
    }

    container.innerHTML = '';

    items.forEach((item) => {
      const itemElement = document.createElement('cart-product-recommendations-item');
      const attributes = this.getItemElementAttributes({ item });
      attributes.forEach((attribute) => {
        itemElement.setAttribute(attribute.name, attribute.value);
      });
      itemElement.innerHTML = item.innerHTML;
      container.appendChild(itemElement);
    });
  };

  getItemElementAttributes = ({ item }) => {
    if (!(item instanceof HTMLElement)){
      return []
    }

    const attributes = []
    for (const attr of item.attributes) {
      attributes.push({
        name: attr.name,
        value: attr.value,
      })
    }
    return attributes
  };

  getProductRecommendations = async ({ targetItems }) => {
    const fetchProductRecommendations = async ({
      productId,
      type = 'related',
    }) => {
      const response = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=10&intent=${type}`,
      ).then((res) => res.json());

      if (response.message) {
        return null;
      }

      return response.products.length ? response.products : null;
    };

    const exclusionTag =
      window?.Shopify?.backInStock?.state === 'waiting' &&
      window?.Shopify?.backInStock?.target_tag
        ? window?.Shopify?.backInStock?.target_tag
        : null;

    const targetDate = new Date();
    targetDate.setMonth(targetDate.getMonth() - 4);

    try {
      const products = [];
      let index = 0;
      do {
        const targetItem = targetItems[index];
        const responseProducts =
          (await fetchProductRecommendations({
            productId: targetItem.product_id,
            type: 'complementary',
          })) ||
          (await fetchProductRecommendations({
            productId: targetItem.product_id,
          }));

        if (responseProducts === null) {
          index++;
          return;
        }

        const filteredItems = responseProducts.filter((product) => {
          const publishedDate = new Date(product.published_at);
          if (publishedDate > targetDate) {
            return (
              !product.tags.includes('hide') &&
              !product.tags.includes(exclusionTag)
            );
          }
          return false;
        });

        const matchProducts = filteredItems.filter((product) =>
          product.options.some((option) =>
            option.values.includes(targetItem.option_value.value),
          ),
        );

        const notMatchProducts = filteredItems.filter(
          (product) =>
            !product.options.some((option) =>
              option.values.includes(targetItem.option_value.value),
            ),
        );

        if (matchProducts.length) {
          const newMatchProducts = [...matchProducts].map((matchProduct) => {
            matchProduct.match_variant_title = targetItem.option_value;
            return matchProduct;
          });
          products.push(...newMatchProducts.slice(0, 5));
        }

        if (products.length < 5) {
          products.push(...notMatchProducts.slice(0, 5 - products.length));
        }

        index++;
      } while (products.length < 5 && targetItems[index + 1]);

      return await Promise.all(
        products.slice(0, 5).map(async (product) => {
          let url = `/products/${product.handle}?view=recommendations`;
          if (product.match_variant_title) {
            url = url + `&target_variant=${product.match_variant_title.value}`;
          }
          url = url + '&end';
          const rawProductCard = await fetch(url).then((res) => res.text());
          return this.parseHTML(rawProductCard);
        }),
      );
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  getCart = async () => {
    try {
      const cart = await fetch('/cart.js').then((res) => res.json());

      if (!cart) {
        return null;
      }

      return cart;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  getProduct = async ({ handle }) => {
    try {
      return await fetch(`/products/${handle}.js`).then((res) => res.json());
    } catch (e) {
      return null;
    }
  };

  onAddToCart = async ({ quantity, variantId }) => {
    if (!quantity || !variantId) {
      return false;
    }

    try {
      const formData = { items: [] };

      formData.items.push({
        id: variantId,
        quantity: quantity,
      });

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      }).then((res) => res.json());

      return addResponse && addResponse?.items?.length > 0;
    } catch (e) {
      console.error(e);

      return false;
    }
  };

  onClose = () => {
    this.classList.add(this.classes.hide);
  };

  initCartObserver = () => {
    const cartObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((rawEntry) => {
        const entry = rawEntry.toJSON();
        const isValidRequestType = ['xmlhttprequest', 'fetch'].includes(
          entry.initiatorType,
        );
        const regex = /\/cart(\/(?!\.js\b)[^ ]*|$)/;
        const isCartChangeRequest = regex.test(entry.name);
        if (isValidRequestType && isCartChangeRequest) {
          this.checkCart();
        }
      });
    });

    cartObserver.observe({ entryTypes: ['resource'] });
  };

  parseHTML = (htmlString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    return doc.body.firstChild;
  };

  arrayChecker = ({ arr1, arr2 }) => {
    const deepEqual = ({ obj1, obj2 }) => {
      if (obj1 === obj2) return true;
      if (
        typeof obj1 !== 'object' ||
        typeof obj2 !== 'object' ||
        obj1 == null ||
        obj2 == null
      )
        return false;

      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);

      if (keys1.length !== keys2.length) return false;

      return keys1.every((key) => deepEqual(obj1[key], obj2[key]));
    };
    if (arr1.length !== arr2.length) return false;

    const sortedArr1 = [...arr1].sort((a, b) =>
      JSON.stringify(a).localeCompare(JSON.stringify(b)),
    );
    const sortedArr2 = [...arr2].sort((a, b) =>
      JSON.stringify(a).localeCompare(JSON.stringify(b)),
    );

    return sortedArr1.every((item, index) =>
      deepEqual(item, sortedArr2[index]),
    );
  };
}

class CartProductRecommendationsItem extends HTMLElement {
  constructor() {
    super();
    this.handle = this.getAttribute('data-product-handle') || null;    
  }

  connectedCallback() {
    this.querySelectorAll('image-element').forEach((imageElement) => {
      imageElement.classList.add('aos-init', 'aos-animate');
    });
    const btnAddToCart = this.querySelector('.recommendation-add-to-cart');
    if (btnAddToCart) {
      btnAddToCart.addEventListener('click', this.handleAddToCart.bind(this));
    }
    this.handle = btnAddToCart.getAttribute('data-product-handle') || null;    
  }

  handleAddToCart = async (e) => {
    const el = e.currentTarget;

    if (!this.handle) {
      return;
    }

    el.classList.add('btn--loading');

    const productData = await this.getProduct({ handle: this.handle });

    if (!productData) {
      return;
    }

    const targetVariant =
      productData.variants.find(
        (variant) => variant.available,
      )

    if (!targetVariant) {
      return;
    }

    const addResponse = await this.onAddToCart({
      variantId: targetVariant.id,
      quantity: 1,
    });

    if (addResponse) {
      document.dispatchEvent(new Event('cart:build'));
    }

    el.classList.remove('btn--loading');
  };

  getProduct = async ({ handle }) => {
    try {
      return await fetch(`/products/${handle}.js`).then((res) => res.json());
    } catch (e) {
      return null;
    }
  };

  onAddToCart = async ({ quantity, variantId }) => {
    if (!quantity || !variantId) {
      return false;
    }

    try {
      const formData = { items: [] };

      formData.items.push({
        id: variantId,
        quantity: quantity,
      });

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      }).then((res) => res.json());

      return addResponse && addResponse?.items?.length > 0;
    } catch (e) {
      console.error(e);

      return false;
    }
  };
}

if (!customElements.get('cart-product-recommendations')) {
  customElements.define(
    'cart-product-recommendations',
    CartProductRecommendations,
  );
}

if(!customElements.get('cart-product-recommendations-item')){
  customElements.define(
    'cart-product-recommendations-item',
    CartProductRecommendationsItem,
  );
}
